<template>
  <nav class="fixed top-0 z-20 w-full bg-cdp-white shadow-md">
    <div class="mx-auto flex flex-wrap items-center justify-between">
      <div class="flex items-center">
        <div class="cursor-pointer" @click="setNavbarRoutes('dataOverview')">
          <icon name="logo-main" class="ml-4 hidden h-14 w-48 lg:flex" />
          <icon name="FeelGroup-web-logo" class="mx-4 h-8 w-8 lg:hidden" />
        </div>
        <ul class="flex w-full flex-wrap text-center text-sm lg:ml-16">
          <li v-for="(list, idx) in showLists" :key="`${idx}-tabLists`" class="mr-2">
            <button
              class="border-b-4 p-4 text-base font-normal duration-300 hover:border-cdp-main-primary-dark hover:text-cdp-black-500 lg:p-4"
              :class="[
                currenArea === list.value
                  ? 'border-cdp-main-primary-dark font-bold text-cdp-black-500'
                  : 'border-transparent font-normal text-cdp-grey-500'
              ]"
              @click="setNavbarRoutes(list.value)"
            >
              {{ t(`route.${list.name}`) }}
            </button>
          </li>
        </ul>
      </div>
      <div class="w-auto">
        <ul class="flex w-full">
          <li>
            <button
              v-if="isDev"
              class="mb-1 flex cursor-pointer px-3 py-4 font-medium text-cdp-black-400 duration-500 hover:bg-gray-100 hover:text-cdp-black-500"
              @click="linkOpen = !linkOpen"
            >
              <icon name="ic_settings" class="mx-2 mt-1 h-5 w-5" />
            </button>
            <!-- super big btn -->
            <div
              v-if="linkOpen"
              class="super-big-btn fixed inset-0 h-full w-full cursor-default"
              @click="linkOpen = !linkOpen"
            />
            <div
              v-show="linkOpen"
              class="top-15 absolute right-28 z-10 w-44 rounded bg-cdp-white font-normal shadow-md duration-300"
            >
              <ul class="divide-y py-1 text-sm text-cdp-black-500">
                <li
                  v-for="(menu, menuIdx) in menuList"
                  :key="`${menuIdx}-menu`"
                  @click="generationData(menu.type)"
                >
                  <button class="block px-4 py-2">
                    {{ menu.name }}
                  </button>
                </li>
              </ul>
            </div>
          </li>
          <li class="p-4">
            <button
              class="flex cursor-pointer items-center text-cdp-main-primary underline duration-500 hover:bg-cdp-grey-100 hover:text-cdp-main-primary-dark"
              @click="toLogout"
            >
              <span>{{ name }}</span
              >, {{ t('logout') }}
            </button>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script>
import { useI18n } from 'vue-i18n';
import lo_ from '@/plugins/lodash.js';

import { storeToRefs } from 'pinia';
import { userStore } from '@/stores/user.js';
import { routerStore } from '@/stores/route.js';
import { permissionStore } from '@/stores/permission.js';

import toast from '@/plugins/sweetalert2.js';

import {
  ApiDataGenerationAll,
  ApiDataGenerationCampaigns,
  ApiDataGenerationClientbases,
  ApiDataGenerationFiltrations,
  ApiDataGenerationTags
} from '@/api/dataGeneration.js';

import { ref, toRaw, computed } from 'vue';
export default {
  name: 'CDPHeader',
  setup() {
    const isDev = import.meta.env.DEV;
    const { t } = useI18n();
    const storeUser = userStore();
    const storeRouter = routerStore();
    const storePermission = permissionStore();

    const { logout } = storeUser;
    const { router, tabLists, tabsPermission } = storeRouter;

    const { currenArea, name } = storeToRefs(storeUser);
    const { allRoute } = storeToRefs(storeRouter);
    const { permission } = storeToRefs(storePermission);

    const linkOpen = ref(false);

    const menuList = computed(() => {
      if (dataGenerationPermission.value?.retrieve) {
        return [
          {
            name: '生成所有測試資料',
            type: 'all'
          },
          {
            name: '生成行銷測試資料',
            type: 'campaign'
          },
          {
            name: '生成會員測試資料',
            type: 'client'
          },
          {
            name: '生成受眾測試資料',
            type: 'filtration'
          },
          {
            name: '生成標籤測試資料',
            type: 'tag'
          }
        ];
      } else {
        return [
          {
            name: '無',
            type: 'none'
          }
        ];
      }
    });

    const dataGenerationPermission = computed(() => {
      return toRaw(permission.value)['data_generation'];
    });

    const showLists = computed(() => {
      let lists = lo_.cloneDeep(tabLists);
      tabLists.forEach((key) => {
        const isDisable = tabsPermission[key.value]?.every(
          (ownPermission) => !toRaw(permission.value)[ownPermission]?.retrieve
        );

        if (isDisable) {
          lists = lists.filter((list) => list.value !== key.value);
        }
      });
      return lists;
    });

    async function generationData(type) {
      let data = null;
      switch (type) {
        case 'all':
          data = await ApiDataGenerationAll();
          break;
        case 'campaign':
          data = await ApiDataGenerationCampaigns();
          break;
        case 'client':
          data = await ApiDataGenerationClientbases();
          break;
        case 'filtration':
          data = await ApiDataGenerationFiltrations();
          break;
        case 'tag':
          data = await ApiDataGenerationTags();
          break;
      }
      if (!data.msg) {
        toast.fire({
          toast: true,
          icon: 'success',
          iconColor: 'green',
          text: '生成成功'
        });
      }
    }

    function setNavbarRoutes(target) {
      // find mother path
      const currentPaths = allRoute.value?.find((item) => item.meta.key === target);

      // check all right permission to enter in
      const currentPassWay = currentPaths?.children.find((route) => {
        if (route.meta.permission) {
          const isPass = route.meta.permission.every(
            (key) => toRaw(permission.value)[key]?.retrieve
          );
          if (isPass) {
            return route.path;
          }
        }
      });

      router.push(currentPassWay.path);
    }

    function toLogout() {
      logout();
      router.push('/login');
    }

    return {
      t,
      currenArea,
      name,
      permission,
      showLists,
      linkOpen,
      menuList,
      generationData,
      toLogout,
      setNavbarRoutes,
      tabsPermission,
      isDev
    };
  }
};
</script>